import type { Registratie } from '@/types/domain';

/**
 * Concepten (autosave). Elk open formulier schrijft periodiek weg naar
 * localStorage, zodat een dichtgeklapte telefoon of verlopen sessie geen
 * werk kost. Een concept verdwijnt zodra de registratie echt is opgeslagen.
 */

const SLEUTEL = 'afkoppelcoach.concepten.v1';
const MAX_LEEFTIJD_MS = 1000 * 60 * 60 * 24 * 14; // 14 dagen

interface Concept {
  registratie: Registratie;
  opgeslagenOp: string;
}

type ConceptOpslag = Record<string, Concept>;

function lees(): ConceptOpslag {
  try {
    const rauw = localStorage.getItem(SLEUTEL);
    return rauw ? (JSON.parse(rauw) as ConceptOpslag) : {};
  } catch {
    return {};
  }
}

function schrijf(opslag: ConceptOpslag): void {
  try {
    localStorage.setItem(SLEUTEL, JSON.stringify(opslag));
  } catch {
    // Opslag vol: concepten zijn een vangnet, geen harde eis.
  }
}

export const draftService = {
  bewaar(registratie: Registratie): string {
    const opslag = lees();
    opslag[registratie.id] = {
      registratie,
      opgeslagenOp: new Date().toISOString(),
    };
    schrijf(opslag);
    return opslag[registratie.id].opgeslagenOp;
  },

  haal(id: string): Concept | null {
    return lees()[id] ?? null;
  },

  verwijder(id: string): void {
    const opslag = lees();
    if (!(id in opslag)) return;
    delete opslag[id];
    schrijf(opslag);
  },

  /** Alle openstaande concepten, nieuwste eerst. */
  alle(): Concept[] {
    return Object.values(lees()).sort((a, b) =>
      b.opgeslagenOp.localeCompare(a.opgeslagenOp)
    );
  },

  /** Ruimt concepten op die ouder zijn dan MAX_LEEFTIJD_MS. */
  opruimen(): void {
    const opslag = lees();
    const grens = Date.now() - MAX_LEEFTIJD_MS;
    let gewijzigd = false;

    for (const [id, concept] of Object.entries(opslag)) {
      if (new Date(concept.opgeslagenOp).getTime() < grens) {
        delete opslag[id];
        gewijzigd = true;
      }
    }
    if (gewijzigd) schrijf(opslag);
  },
};

export type { Concept };
